/**
 * render/ability_ui.ts — The Skill Bar and Emote Wheel
 *
 * Two overlays that live between the HUD and the world:
 *
 * 1. **Skill Bar**: 6 ability slots (keys 1-6) above the action dock. Shows
 *    cooldown sweeps, EP cost, key bindings. Plus the Ember Points bar.
 *    Plus the active buff strip.
 *
 * 2. **Emote Wheel**: Radial selector (E key) with 16 emotes. Mouse angle
 *    picks the slice; release or click fires the emote.
 *
 * Both follow the warm wood/gold palette. Both are hot-path code — no
 * allocations, no string concat in tight loops.
 */

import type { GameState, AbilityState, ActiveBuff } from "../engine/state.js";
import { getAbility, type AbilityDefinition } from "../combat/skills.js";
import { BUFF_META } from "../combat/buffs.js";
import { EMOTES, type EmoteDefinition } from "../combat/emotes.js";

// ─── Palette (shared with hud.ts / combat_ui.ts) ─────────────────────────

const P = {
  woodDark:   "#1e1508",
  woodMid:    "#2a1d0e",
  woodLight:  "#342414",
  goldDark:   "#6b4f0c",
  goldMid:    "#9b7318",
  goldBright: "#d4a030",
  goldGlow:   "#f0c860",
  textPrime:  "#f0d88a",
  textDim:    "#c0a060",
  textMuted:  "#7a6838",
  slotBg:     "#0c0804",
  slotBorder: "#6b4f0c",
  slotEmpty:  "#2a2018",
  // Ember bar
  emberFull:  "#d4a030",
  emberLow:   "#6b4f0c",
  emberBg:    "#0e0a04",
  // Cooldown
  cdOverlay:  "rgba(0, 0, 0, 0.65)",
  cdReady:    "rgba(212, 160, 48, 0.3)",
  // Buff colors
  buffPositive: "#44AA88",
  buffNegative: "#CC4444",
  // Emote wheel
  wheelBg:    "rgba(14, 10, 4, 0.88)",
  wheelSlice: "rgba(42, 29, 14, 0.9)",
  wheelHover: "rgba(107, 79, 12, 0.5)",
  wheelBorder:"#6b4f0c",
  wheelText:  "#f0d88a",
} as const;

// ─── Layout Constants ─────────────────────────────────────────────────────

// Skill bar sits above the HUD dock (BAR_H = 72)
const SKILL_SLOT_SIZE  = 40;
const SKILL_SLOT_GAP   = 6;
const SKILL_BAR_SLOTS  = 6;
const SKILL_BAR_W      = SKILL_BAR_SLOTS * SKILL_SLOT_SIZE + (SKILL_BAR_SLOTS - 1) * SKILL_SLOT_GAP;
const SKILL_BAR_PAD    = 8;
const SKILL_BAR_H      = SKILL_SLOT_SIZE + SKILL_BAR_PAD * 2;
const SKILL_BAR_BOTTOM_GAP = 80; // gap above canvas bottom (dock is at bottom)

// Ember bar sits beneath the skill slots
const EP_BAR_W     = SKILL_BAR_W;
const EP_BAR_H     = 6;
const EP_BAR_GAP   = 4;

// Buff strip sits above the skill bar
const BUFF_ICON_SIZE = 22;
const BUFF_ICON_GAP  = 3;
const BUFF_STRIP_GAP = 6;

// Emote wheel
const WHEEL_INNER_R  = 35;
const WHEEL_OUTER_R  = 120;
const WHEEL_ICON_R   = 78; // radius at which emote icons sit

// ─── Canvas Helpers ──────────────────────────────────────────────────────

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.arcTo(x + w, y,     x + w, y + radius,     radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
  ctx.lineTo(x + radius, y + h);
  ctx.arcTo(x, y + h,    x, y + h - radius,       radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y,        x + radius, y,            radius);
  ctx.closePath();
}

// ═══════════════════════════════════════════════════════════════════════════
//  SKILL BAR RENDERER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Draw the 6-slot ability bar, EP bar, and active buff strip.
 * Positioned center-bottom, just above the HUD action dock.
 */
export function drawSkillBar(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  timestamp: number,
  mouseX: number,
  mouseY: number,
): void {
  const cw = state.canvasWidth;
  const ch = state.canvasHeight;
  const bar = state.player.abilityBar;
  const ember = state.player.combat.ember;

  // Position: centered horizontally, above the dock
  const barX = Math.round((cw - SKILL_BAR_W) / 2);
  const barY = ch - SKILL_BAR_BOTTOM_GAP - SKILL_BAR_H;

  // ── Skill Slots ────────────────────────────────────────────────────────

  for (let i = 0; i < SKILL_BAR_SLOTS; i++) {
    const slot = bar.slots[i];
    const sx = barX + i * (SKILL_SLOT_SIZE + SKILL_SLOT_GAP);
    const sy = barY + SKILL_BAR_PAD;

    // Slot background
    ctx.fillStyle = P.slotBg;
    roundRect(ctx, sx, sy, SKILL_SLOT_SIZE, SKILL_SLOT_SIZE, 4);
    ctx.fill();

    // Border
    ctx.strokeStyle = P.slotBorder;
    ctx.lineWidth = 1.5;
    roundRect(ctx, sx, sy, SKILL_SLOT_SIZE, SKILL_SLOT_SIZE, 4);
    ctx.stroke();

    if (slot) {
      const def = getAbility(slot.id);
      if (def) {
        drawAbilitySlot(ctx, sx, sy, SKILL_SLOT_SIZE, slot, def, ember.current, timestamp, bar.globalCooldownEnd);
      }
    } else {
      // Empty slot — faint border pattern
      ctx.fillStyle = P.slotEmpty;
      roundRect(ctx, sx + 3, sy + 3, SKILL_SLOT_SIZE - 6, SKILL_SLOT_SIZE - 6, 2);
      ctx.fill();
    }

    // Key binding label (bottom-right corner)
    ctx.font = "bold 9px monospace";
    ctx.fillStyle = P.textMuted;
    ctx.textAlign = "right";
    ctx.textBaseline = "bottom";
    ctx.fillText(`${i + 1}`, sx + SKILL_SLOT_SIZE - 3, sy + SKILL_SLOT_SIZE - 2);

    // Hover tooltip
    if (slot && mouseX >= sx && mouseX < sx + SKILL_SLOT_SIZE &&
        mouseY >= sy && mouseY < sy + SKILL_SLOT_SIZE) {
      const def = getAbility(slot.id);
      if (def) drawAbilityTooltip(ctx, sx, sy, def, ember.current, state.canvasWidth);
    }
  }

  // ── Ember Points Bar ───────────────────────────────────────────────────

  const epY = barY + SKILL_BAR_H + EP_BAR_GAP;
  const epRatio = Math.max(0, Math.min(1, ember.current / ember.max));

  // BG
  ctx.fillStyle = P.emberBg;
  roundRect(ctx, barX, epY, EP_BAR_W, EP_BAR_H, 3);
  ctx.fill();

  // Fill
  if (epRatio > 0) {
    const fillW = Math.round(EP_BAR_W * epRatio);
    const grad = ctx.createLinearGradient(barX, epY, barX + fillW, epY);
    grad.addColorStop(0, P.emberLow);
    grad.addColorStop(1, P.emberFull);
    ctx.fillStyle = grad;
    roundRect(ctx, barX, epY, fillW, EP_BAR_H, 3);
    ctx.fill();

    // Regen shimmer — travelling highlight when EP is below max
    if (epRatio < 0.99) {
      const shimmerT = (timestamp * 0.001) % 1.5 / 1.5; // 1.5s cycle
      const shimmerX = barX + shimmerT * fillW;
      const shimmerW = 20;
      ctx.save();
      // Clip to fill area
      roundRect(ctx, barX, epY, fillW, EP_BAR_H, 3);
      ctx.clip();
      const sg = ctx.createLinearGradient(shimmerX - shimmerW, epY, shimmerX + shimmerW, epY);
      sg.addColorStop(0, "rgba(255, 220, 120, 0)");
      sg.addColorStop(0.5, "rgba(255, 220, 120, 0.25)");
      sg.addColorStop(1, "rgba(255, 220, 120, 0)");
      ctx.fillStyle = sg;
      ctx.fillRect(shimmerX - shimmerW, epY, shimmerW * 2, EP_BAR_H);
      ctx.restore();
    }
  }

  // Border
  ctx.strokeStyle = P.goldDark;
  ctx.lineWidth = 1;
  roundRect(ctx, barX, epY, EP_BAR_W, EP_BAR_H, 3);
  ctx.stroke();

  // EP text
  ctx.font = "bold 8px monospace";
  ctx.fillStyle = P.textPrime;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(`${Math.floor(ember.current)} / ${ember.max} EP`, barX + EP_BAR_W / 2, epY + EP_BAR_H / 2 + 1);

  // ── Active Buff Strip ──────────────────────────────────────────────────

  drawBuffStrip(ctx, state, barX, barY - BUFF_STRIP_GAP - BUFF_ICON_SIZE, timestamp, mouseX, mouseY);
}

// ─── Individual Ability Slot ──────────────────────────────────────────────

function drawAbilitySlot(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, size: number,
  slot: AbilityState,
  def: AbilityDefinition,
  currentEP: number,
  timestamp: number,
  gcdEnd: number,
): void {
  const now = timestamp;
  const onCooldown = now < slot.cooldownEnd;
  const onGCD = now < gcdEnd;
  const notEnoughEP = currentEP < def.cost;
  const unusable = onCooldown || onGCD || notEnoughEP;

  // Icon (emoji centered)
  ctx.font = `${size * 0.55}px serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  if (unusable) {
    ctx.globalAlpha = 0.4;
  }
  ctx.fillText(def.icon, x + size / 2, y + size / 2);
  ctx.globalAlpha = 1.0;

  // Cooldown sweep overlay
  if (onCooldown) {
    const cdTotal = def.cooldown;
    const cdRemain = slot.cooldownEnd - now;
    const cdFraction = Math.max(0, Math.min(1, cdRemain / cdTotal));

    // Dark sweep (pie-clock style)
    ctx.fillStyle = P.cdOverlay;
    ctx.beginPath();
    ctx.moveTo(x + size / 2, y + size / 2);
    const startAngle = -Math.PI / 2;
    const endAngle = startAngle + Math.PI * 2 * cdFraction;
    ctx.arc(x + size / 2, y + size / 2, size / 2, startAngle, endAngle);
    ctx.closePath();
    ctx.fill();

    // Cooldown seconds text
    ctx.font = "bold 12px monospace";
    ctx.fillStyle = P.textPrime;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${Math.ceil(cdRemain)}`, x + size / 2, y + size / 2);
  }

  // GCD indicator (subtle border glow)
  if (onGCD && !onCooldown) {
    ctx.strokeStyle = P.goldGlow;
    ctx.lineWidth = 2;
    roundRect(ctx, x, y, size, size, 4);
    ctx.stroke();
  }

  // Not enough EP indicator (red tint at bottom)
  if (notEnoughEP && !onCooldown) {
    ctx.fillStyle = "rgba(180, 40, 40, 0.3)";
    roundRect(ctx, x, y + size * 0.7, size, size * 0.3, 0);
    ctx.fill();
  }

  // EP cost (top-left, small)
  ctx.font = "bold 8px monospace";
  ctx.fillStyle = notEnoughEP ? "#cc4444" : P.textDim;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(`${def.cost}`, x + 3, y + 2);

  // Charges (bottom-left)
  if (def.maxCharges > 1) {
    ctx.font = "bold 9px monospace";
    ctx.fillStyle = slot.charges > 0 ? P.goldBright : "#cc4444";
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillText(`${slot.charges}`, x + 3, y + size - 2);
  }

  // Ready flash (pulse when coming off cooldown)
  if (!onCooldown && slot.cooldownEnd > 0 && now - slot.cooldownEnd < 0.5) {
    const t = (now - slot.cooldownEnd) / 0.5;
    ctx.strokeStyle = P.goldGlow;
    ctx.lineWidth = 2;
    ctx.globalAlpha = 1 - t;
    roundRect(ctx, x - 2, y - 2, size + 4, size + 4, 6);
    ctx.stroke();
    ctx.globalAlpha = 1.0;
  }
}

// ─── Ability Tooltip ──────────────────────────────────────────────────────

function drawAbilityTooltip(
  ctx: CanvasRenderingContext2D,
  slotX: number, slotY: number,
  def: AbilityDefinition,
  currentEP: number,
  canvasWidth: number,
): void {
  const tipW = 200;
  const lineH = 14;
  const pad = 8;

  // Build lines
  const lines: string[] = [
    def.name,
    `${def.cost} EP · ${def.cooldown > 0 ? def.cooldown + "s CD" : "No CD"}`,
    "",
    ...wrapText(def.description, 30),
  ];

  if (def.maxCharges > 1) {
    lines.push(`${def.maxCharges} charges (${def.chargeRechargeTime}s recharge)`);
  }

  const tipH = pad * 2 + lines.length * lineH;
  let tipX = slotX + SKILL_SLOT_SIZE / 2 - tipW / 2;
  let tipY = slotY - tipH - 8;

  // Clamp to canvas
  tipX = Math.max(4, Math.min(canvasWidth - tipW - 4, tipX));
  if (tipY < 4) tipY = slotY + SKILL_SLOT_SIZE + 4;

  // Shadow
  ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
  roundRect(ctx, tipX + 2, tipY + 2, tipW, tipH, 5);
  ctx.fill();

  // Background
  ctx.fillStyle = P.woodDark;
  roundRect(ctx, tipX, tipY, tipW, tipH, 5);
  ctx.fill();

  // Border
  ctx.strokeStyle = P.goldDark;
  ctx.lineWidth = 1.5;
  roundRect(ctx, tipX, tipY, tipW, tipH, 5);
  ctx.stroke();

  // Inner glow
  ctx.strokeStyle = "rgba(107, 79, 12, 0.2)";
  ctx.lineWidth = 1;
  roundRect(ctx, tipX + 2, tipY + 2, tipW - 4, tipH - 4, 4);
  ctx.stroke();

  // Text
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  let ly = tipY + pad;

  for (let i = 0; i < lines.length; i++) {
    if (i === 0) {
      // Name — bold gold
      ctx.font = "bold 11px monospace";
      ctx.fillStyle = P.goldBright;
    } else if (i === 1) {
      // Cost/CD line
      ctx.font = "10px monospace";
      ctx.fillStyle = currentEP < def.cost ? "#cc4444" : P.textDim;
    } else if (lines[i] === "") {
      ly += lineH * 0.3;
      continue;
    } else {
      ctx.font = "10px monospace";
      ctx.fillStyle = P.textPrime;
    }
    ctx.fillText(lines[i], tipX + pad, ly);
    ly += lineH;
  }
}

// ─── Text Wrapping ────────────────────────────────────────────────────────

function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    if (current.length + word.length + 1 > maxChars && current.length > 0) {
      lines.push(current);
      current = word;
    } else {
      current = current.length > 0 ? current + " " + word : word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

// ═══════════════════════════════════════════════════════════════════════════
//  ACTIVE BUFF STRIP
// ═══════════════════════════════════════════════════════════════════════════

function drawBuffStrip(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  x: number, y: number,
  timestamp: number,
  mouseX: number, mouseY: number,
): void {
  const buffs = state.player.activeBuffs;
  if (buffs.length === 0) return;

  const now = timestamp;

  for (let i = 0; i < buffs.length; i++) {
    const buff = buffs[i];
    const bx = x + i * (BUFF_ICON_SIZE + BUFF_ICON_GAP);
    const by = y;
    const meta = BUFF_META[buff.type];

    // Background
    ctx.fillStyle = buff.isDebuff ? "rgba(140, 30, 30, 0.5)" : "rgba(30, 80, 60, 0.5)";
    roundRect(ctx, bx, by, BUFF_ICON_SIZE, BUFF_ICON_SIZE, 3);
    ctx.fill();

    // Border (color-coded)
    ctx.strokeStyle = meta.color;
    ctx.lineWidth = 1.5;
    roundRect(ctx, bx, by, BUFF_ICON_SIZE, BUFF_ICON_SIZE, 3);
    ctx.stroke();

    // Icon
    ctx.font = `${BUFF_ICON_SIZE * 0.6}px serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(buff.icon, bx + BUFF_ICON_SIZE / 2, by + BUFF_ICON_SIZE / 2);

    // Stack count (top-right)
    if (buff.stacks > 1) {
      ctx.font = "bold 8px monospace";
      ctx.fillStyle = P.textPrime;
      ctx.textAlign = "right";
      ctx.textBaseline = "top";
      ctx.fillText(`${buff.stacks}`, bx + BUFF_ICON_SIZE - 1, by + 1);
    }

    // Duration remaining — pulse border when about to expire
    const timeLeft = Math.max(0, buff.expiresAt - now);
    if (timeLeft < 5 && timeLeft > 0) {
      // Pulsing when about to expire
      const pulse = Math.sin(now * 6) * 0.3 + 0.7;
      ctx.globalAlpha = pulse;
      ctx.strokeStyle = "#ff4444";
      ctx.lineWidth = 2;
      roundRect(ctx, bx - 1, by - 1, BUFF_ICON_SIZE + 2, BUFF_ICON_SIZE + 2, 4);
      ctx.stroke();
      ctx.globalAlpha = 1.0;
    }

    // Duration text (below icon)
    if (timeLeft > 0) {
      ctx.font = "7px monospace";
      ctx.fillStyle = timeLeft < 3 ? "#ff6644" : P.textDim;
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(
        timeLeft >= 60 ? `${Math.floor(timeLeft / 60)}m` : `${Math.ceil(timeLeft)}s`,
        bx + BUFF_ICON_SIZE / 2,
        by + BUFF_ICON_SIZE + 1,
      );
    }

    // Hover tooltip
    if (mouseX >= bx && mouseX < bx + BUFF_ICON_SIZE &&
        mouseY >= by && mouseY < by + BUFF_ICON_SIZE + 10) {
      drawBuffTooltip(ctx, bx, by, buff, timeLeft, state.canvasWidth);
    }
  }
}

// ─── Buff Tooltip ─────────────────────────────────────────────────────────

function drawBuffTooltip(
  ctx: CanvasRenderingContext2D,
  bx: number, by: number,
  buff: ActiveBuff,
  timeLeft: number,
  canvasWidth: number,
): void {
  const meta = BUFF_META[buff.type];
  const tipW = 160;
  const pad = 6;
  const lineH = 13;

  const lines: string[] = [
    buff.name + (buff.stacks > 1 ? ` x${buff.stacks}` : ""),
    `${Math.ceil(timeLeft)}s remaining`,
  ];

  if (buff.value > 0) {
    lines.push(`Value: ${Math.round(buff.value * buff.stacks)}`);
  }

  const tipH = pad * 2 + lines.length * lineH;
  let tipX = bx + BUFF_ICON_SIZE / 2 - tipW / 2;
  let tipY = by - tipH - 6;
  tipX = Math.max(4, Math.min(canvasWidth - tipW - 4, tipX));

  // Background
  ctx.fillStyle = P.woodDark;
  roundRect(ctx, tipX, tipY, tipW, tipH, 4);
  ctx.fill();
  ctx.strokeStyle = meta.color;
  ctx.lineWidth = 1;
  roundRect(ctx, tipX, tipY, tipW, tipH, 4);
  ctx.stroke();

  // Text
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  let ly = tipY + pad;
  for (let i = 0; i < lines.length; i++) {
    ctx.font = i === 0 ? "bold 10px monospace" : "9px monospace";
    ctx.fillStyle = i === 0 ? meta.color : P.textPrime;
    ctx.fillText(lines[i], tipX + pad, ly);
    ly += lineH;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  EMOTE WHEEL RENDERER
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Draw the radial emote wheel centered on screen.
 * Called when state.player.emoteWheel.open is true.
 */
export function drawEmoteWheel(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  timestamp: number,
  mouseX: number,
  mouseY: number,
): void {
  const wheel = state.player.emoteWheel;
  if (!wheel.open) return;

  const cx = state.canvasWidth / 2;
  const cy = state.canvasHeight / 2;
  const emoteCount = EMOTES.length;
  const sliceAngle = (Math.PI * 2) / emoteCount;

  // Entrance animation
  const openElapsed = timestamp - wheel.openedAt;
  const animT = Math.min(1, openElapsed / 0.2); // 200ms open
  const scale = 0.6 + 0.4 * easeOutBack(animT);
  const alpha = animT;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(cx, cy);
  ctx.scale(scale, scale);

  // ── Background dim ─────────────────────────────────────────────────────
  // Subtle vignette around the wheel
  const vignette = ctx.createRadialGradient(0, 0, WHEEL_INNER_R, 0, 0, WHEEL_OUTER_R * 1.5);
  vignette.addColorStop(0, "rgba(0, 0, 0, 0)");
  vignette.addColorStop(1, "rgba(0, 0, 0, 0.35)");
  ctx.fillStyle = vignette;
  ctx.beginPath();
  ctx.arc(0, 0, WHEEL_OUTER_R * 1.5, 0, Math.PI * 2);
  ctx.fill();

  // ── Determine selected slice ───────────────────────────────────────────
  const dx = mouseX - cx;
  const dy = mouseY - cy;
  const dist = Math.sqrt(dx * dx + dy * dy);
  let selectedIndex = -1;

  if (dist > WHEEL_INNER_R * scale) {
    // Mouse angle — atan2 gives angle from positive X axis, we want from top
    let angle = Math.atan2(dy, dx) + Math.PI / 2;
    if (angle < 0) angle += Math.PI * 2;
    selectedIndex = Math.floor(angle / sliceAngle) % emoteCount;
  }

  // Write selection back to state
  wheel.selectedIndex = selectedIndex;

  // ── Draw slices ────────────────────────────────────────────────────────
  for (let i = 0; i < emoteCount; i++) {
    const emote = EMOTES[i];
    const startA = i * sliceAngle - Math.PI / 2;
    const endA = startA + sliceAngle;
    const isSelected = i === selectedIndex;

    // Slice fill
    ctx.beginPath();
    ctx.arc(0, 0, WHEEL_OUTER_R, startA, endA);
    ctx.arc(0, 0, WHEEL_INNER_R, endA, startA, true);
    ctx.closePath();

    if (isSelected) {
      ctx.fillStyle = P.wheelHover;
    } else {
      ctx.fillStyle = P.wheelSlice;
    }
    ctx.fill();

    // Slice border
    ctx.strokeStyle = P.wheelBorder;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Selected highlight glow
    if (isSelected) {
      ctx.strokeStyle = P.goldBright;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Emote icon
    const midAngle = startA + sliceAngle / 2;
    const iconX = Math.cos(midAngle) * WHEEL_ICON_R;
    const iconY = Math.sin(midAngle) * WHEEL_ICON_R;

    ctx.font = isSelected ? "22px serif" : "18px serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#FFFFFF";
    ctx.fillText(emote.symbol, iconX, iconY);

    // Name text for selected
    if (isSelected) {
      // Draw name in center of wheel
      ctx.font = "bold 12px monospace";
      ctx.fillStyle = P.wheelText;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(emote.name, 0, -6);

      // Category hint
      ctx.font = "9px monospace";
      ctx.fillStyle = P.textDim;
      ctx.fillText(emote.category, 0, 8);
    }
  }

  // ── Center circle ──────────────────────────────────────────────────────
  ctx.beginPath();
  ctx.arc(0, 0, WHEEL_INNER_R, 0, Math.PI * 2);
  ctx.fillStyle = P.woodDark;
  ctx.fill();
  ctx.strokeStyle = P.goldDark;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Center text when nothing selected
  if (selectedIndex < 0) {
    ctx.font = "bold 11px monospace";
    ctx.fillStyle = P.textDim;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("Emotes", 0, -4);
    ctx.font = "8px monospace";
    ctx.fillStyle = P.textMuted;
    ctx.fillText("[E] to close", 0, 10);
  }

  ctx.restore();
}

// ─── Floating Emote Renderer ──────────────────────────────────────────────

/**
 * Draw floating emote symbols above characters.
 * These are spawned by triggerEmote() and float upward with fade.
 */
export function drawFloatingEmotes(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  cameraX: number,
  cameraY: number,
  zoom: number,
  timestamp: number,
): void {
  const emotes = state.player.floatingEmotes;
  if (emotes.length === 0) return;

  const FLOAT_LIFETIME = 2.0;
  const FLOAT_RISE = 40; // pixels upward over lifetime

  for (const emote of emotes) {
    const age = timestamp - emote.createdAt;
    if (age >= FLOAT_LIFETIME) continue;

    const t = age / FLOAT_LIFETIME;
    const alpha = t < 0.1 ? t / 0.1 : t > 0.7 ? 1 - (t - 0.7) / 0.3 : 1.0;
    const rise = FLOAT_RISE * easeOutQuad(t);
    const scale = 1 + 0.2 * Math.sin(t * Math.PI); // gentle pulse

    // Convert world-space coords to screen with camera
    const sx = (emote.x - cameraX) * zoom + state.canvasWidth / 2;
    const sy = (emote.y - cameraY) * zoom + state.canvasHeight / 2 - rise * zoom;

    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(sx, sy);
    ctx.scale(scale, scale);

    // Shadow behind the symbol
    ctx.font = "22px serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
    ctx.fillText(emote.symbol, 1, 1);

    // The emote symbol
    ctx.fillStyle = emote.color;
    ctx.fillText(emote.symbol, 0, 0);

    ctx.restore();
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  ENHANCED DAMAGE NUMBERS
// ═══════════════════════════════════════════════════════════════════════════

/** Color table for each damage type */
const DAMAGE_TYPE_COLORS: Record<string, { fill: string; stroke: string; glow: string }> = {
  physical: { fill: "#f0e8d0", stroke: "#2a1d0e", glow: "rgba(240, 232, 208, 0.3)" },
  fire:     { fill: "#ff6622", stroke: "#441100", glow: "rgba(255, 102, 34, 0.4)" },
  nature:   { fill: "#44cc44", stroke: "#0a2a0a", glow: "rgba(68, 204, 68, 0.3)" },
  frost:    { fill: "#88ccff", stroke: "#0a1a2a", glow: "rgba(136, 204, 255, 0.3)" },
  shadow:   { fill: "#aa66ee", stroke: "#1a0a2a", glow: "rgba(170, 102, 238, 0.3)" },
  heal:     { fill: "#44ff88", stroke: "#0a2a10", glow: "rgba(68, 255, 136, 0.4)" },
  miss:     { fill: "#888888", stroke: "#1a1a1a", glow: "rgba(136, 136, 136, 0.2)" },
  dodge:    { fill: "#aaaacc", stroke: "#1a1a2a", glow: "rgba(170, 170, 204, 0.2)" },
  block:    { fill: "#8888aa", stroke: "#1a1a22", glow: "rgba(136, 136, 170, 0.2)" },
  combo_break: { fill: "#ff4444", stroke: "#2a0808", glow: "rgba(255, 68, 68, 0.5)" },
};

/** Text labels for non-numeric damage types */
const DAMAGE_TYPE_LABELS: Record<string, string> = {
  miss:  "MISS",
  dodge: "DODGE",
  block: "BLOCK",
  combo_break: "COMBO BROKEN",
};

/**
 * Enhanced damage number renderer with type-based colors, directional float,
 * crit scaling, and status text.
 */
export function drawEnhancedDamageNumber(
  ctx: CanvasRenderingContext2D,
  value: number,
  damageType: string,
  isCrit: boolean,
  isPlayerDamage: boolean,
  age: number,       // 0.0 to 1.0 (normalized lifetime)
  screenX: number,
  screenY: number,
  angle: number,     // float direction in radians
): void {
  const LIFETIME = 1.2;
  const t = age / LIFETIME;
  if (t >= 1) return;

  const colors = DAMAGE_TYPE_COLORS[damageType] ?? DAMAGE_TYPE_COLORS.physical;
  const label = DAMAGE_TYPE_LABELS[damageType];

  // Float distance — parabolic arc
  const floatDist = 40 * easeOutQuad(t);
  const dx = Math.cos(angle) * floatDist;
  const dy = Math.sin(angle) * floatDist;

  // Alpha — fade out in last 30%
  const alpha = t > 0.7 ? 1 - (t - 0.7) / 0.3 : 1.0;

  // Size — crits are bigger, with an initial pop
  const baseSize = isCrit ? 18 : 13;
  const popScale = t < 0.1 ? 1 + (1 - t / 0.1) * (isCrit ? 0.5 : 0.2) : 1.0;
  const fontSize = Math.round(baseSize * popScale);

  const displayText = label ?? (value === 0 ? "0" : `${value}`);

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.translate(screenX + dx, screenY + dy);

  // Crit — golden glow pulse
  if (isCrit && !label) {
    const pulse = 0.5 + 0.5 * Math.sin(t * Math.PI * 4);
    ctx.shadowColor = "#ffcc44";
    ctx.shadowBlur = 8 + pulse * 6;
  }

  // Player damage is always red override for clarity
  const fillColor = isPlayerDamage ? "#e84040" : colors.fill;
  const strokeColor = isPlayerDamage ? "#1a0808" : colors.stroke;

  // Text stroke + fill
  ctx.font = `bold ${fontSize}px monospace`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = 3;
  ctx.strokeStyle = strokeColor;
  ctx.strokeText(displayText, 0, 0);
  ctx.fillStyle = fillColor;
  ctx.fillText(displayText, 0, 0);

  // Crit star burst (tiny triangles around text)
  if (isCrit && !label && t < 0.3) {
    const burstAlpha = 1 - t / 0.3;
    ctx.globalAlpha = alpha * burstAlpha;
    ctx.fillStyle = "#ffcc44";
    const burstR = fontSize * 0.8 + t * 30;
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI * 2 * i) / 6 + t * 3;
      const bx = Math.cos(a) * burstR;
      const by = Math.sin(a) * burstR;
      ctx.beginPath();
      ctx.arc(bx, by, 2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  ctx.restore();
}

// ─── Easing Functions ─────────────────────────────────────────────────────

function easeOutQuad(t: number): number {
  return t * (2 - t);
}

function easeOutBack(t: number): number {
  const s = 1.70158;
  return 1 + (t - 1) * (t - 1) * ((s + 1) * (t - 1) + s);
}
